// ═══════════════════════════════════════════════════════════════════════════
// DOMAIN: mascotte « Professeur Ornaxion » — règles pures d'apparition
// ═══════════════════════════════════════════════════════════════════════════
//
// Garde d'apparition et tirage de phrase, extraits ici pour être testables hors
// DOM (vitest) et rester la source de vérité unique. Le composant volet
// (component-mascot.ts) ne fait qu'appeler ces fonctions + lire/écrire l'état
// de cooldown (localStorage isolé par tenant, cf. tlsGet/tlsSet).
//
// Ordre de garde (cf. docs/SPEC_MASCOTTE_ORNAXION.md §2) :
//   pas mobile → préférence user → cooldown écoulé → tirage de fréquence.
// Le tirage (fréquence) est évalué APRÈS le cooldown, pour qu'un tirage raté
// ne consomme pas le cooldown.

export interface MascotDecisionInput {
  /** vrai si l'app est en affichage mobile (jamais d'apparition) */
  isMobile: boolean;
  /** préférence utilisateur « Bénéficier des conseils » */
  prefEnabled: boolean;
  /** délai minimal entre deux apparitions (minutes) ; 0 = désactivé */
  cooldownMinutes: number;
  /** fréquence d'apparition en % (0-100) */
  frequencyPercent: number;
  /** horodatage courant (ms epoch) */
  now: number;
  /** horodatage de la dernière apparition (ms epoch) ou null */
  lastShownAt: number | null;
  /** tirage aléatoire dans [0, 1) — injecté pour testabilité */
  rand: number;
}

/**
 * Cooldown écoulé ? Vrai si jamais montré, si le délai est désactivé (≤ 0),
 * ou si `now` dépasse `lastShownAt + cooldownMinutes`.
 */
export function cooldownElapsed(
  now: number,
  lastShownAt: number | null,
  cooldownMinutes: number,
): boolean {
  if (cooldownMinutes <= 0) return true;
  if (lastShownAt == null) return true;
  return now >= lastShownAt + cooldownMinutes * 60_000;
}

/**
 * Tirage de fréquence réussi ? `rand` ∈ [0,1). 0 % ne montre jamais, 100 %
 * montre toujours.
 */
export function passesFrequency(rand: number, frequencyPercent: number): boolean {
  if (frequencyPercent <= 0) return false;
  if (frequencyPercent >= 100) return true;
  return rand * 100 < frequencyPercent;
}

/**
 * Décision finale d'apparition (garde complète), dans l'ordre spécifié.
 */
export function shouldShowMascot(i: MascotDecisionInput): boolean {
  if (i.isMobile) return false;
  if (!i.prefEnabled) return false;
  if (!cooldownElapsed(i.now, i.lastShownAt, i.cooldownMinutes)) return false;
  return passesFrequency(i.rand, i.frequencyPercent);
}

/**
 * Tire une phrase au hasard parmi `phrases`, en excluant `lastId` (la dernière
 * montrée pour ce cas) tant qu'il reste au moins 2 phrases — évite la
 * répétition immédiate. Renvoie `null` si la liste est vide.
 */
export function pickPhrase<T extends { id: number }>(
  phrases: readonly T[],
  lastId: number | null,
  rand: number,
): T | null {
  if (!phrases.length) return null;
  let pool = phrases as readonly T[];
  if (lastId != null && phrases.length >= 2) {
    const filtered = phrases.filter((p) => p.id !== lastId);
    if (filtered.length) pool = filtered;
  }
  const idx = Math.min(pool.length - 1, Math.floor(rand * pool.length));
  return pool[idx] ?? null;
}
